import { createSelector } from "reselect";
import get from "src/lib/get";
import isEmpty from "src/lib/isEmpty";
import { MIN65, FIN30X1 } from "src/data/product-data";
import { containsAllHairLossProducts } from "src/data/helpers";
import {
  getHairLossStatus,
  getIdentityCheckPassed,
} from "src/redux/account/subscription/selectors";
import { getCoupon as getSubscribeCoupon } from "src/redux/coupons/selectors";
import { getConsultationIdCheck } from "src/redux/consultations/id-check/selectors";
import { couponDiscountPercentage, parseConsultationAnswers } from "../helpers";

export function getConsultation(state = {}) {
  return state.hairLossConsultation || {};
}

export const getConsultationAnswers = createSelector(
  getConsultation,
  (consultation) => consultation.answers || {}
);

export const getCanSubmitHairLossMainConsultation = createSelector(
  getConsultationAnswers,
  (consultationAnswers) => {
    const answers = { ...consultationAnswers };
    delete answers.terms;
    return (
      !isEmpty(answers) &&
      Object.keys(answers).every((key) => !isEmpty(answers[key]))
    );
  }
);

export const getConsultationHairLossCoupons = createSelector(
  getConsultation,
  (consultation) => {
    const consultationCoupons = get(consultation, "coupons.data", []);
    return consultationCoupons.filter((coupon) => {
      const couponProducts = get(coupon, "products.data", []);
      return containsAllHairLossProducts(couponProducts);
    });
  }
);

export const getConsultationHairLossCouponsError = createSelector(
  getConsultation,
  (consultation) => get(consultation, "coupons.error", null)
);

export const getConsultationCanHaveCouponDiscount = createSelector(
  [
    getHairLossStatus,
    getConsultationHairLossCoupons,
    getConsultationHairLossCouponsError,
  ],
  (hairLossStatus, coupons = [], couponsError) => {
    return (
      (hairLossStatus === "not_started" || hairLossStatus === "failed") &&
      !coupons.length &&
      !couponsError
    );
  }
);

export const getConsultationCouponDiscountPercentage = createSelector(
  getConsultationHairLossCoupons,
  (coupons = []) => {
    return couponDiscountPercentage({
      couponData: coupons[0],
      applicableDiscountTypes: ["percentage_products_and"],
    });
  }
);

export const getConsultationSubscribeHairLossCouponDiscountPercentage = createSelector(
  getSubscribeCoupon,
  (subscribeCoupon = {}) => {
    if (
      containsAllHairLossProducts(get(subscribeCoupon, "products.data", [])) ||
      subscribeCoupon.discount_amount_type === "percentage"
    ) {
      return couponDiscountPercentage({
        couponData: subscribeCoupon,
        applicableDiscountTypes: ["percentage_products_and", "percentage"],
      });
    }
    return 0;
  }
);

export const getConsultationDiscountedPrice = createSelector(
  getConsultationCouponDiscountPercentage,
  (discountPercentage = 0) => {
    return (MIN65.priceInPence + FIN30X1.priceInPence) * discountPercentage;
  }
);

export const getConsultationProductOptions = createSelector(
  getConsultation,
  (consultation) => consultation.productOptions || {}
);

export const getConsultationProduct = createSelector(
  getConsultationProductOptions,
  (productOptions) => productOptions.product || {}
);

export const getCanDoConsultation = createSelector(
  getHairLossStatus,
  (hairLossStatus) => {
    return hairLossStatus !== "pending" && hairLossStatus !== "approved";
  }
);

export const getFormattedNewConsultation = createSelector(
  [
    getConsultationAnswers,
    getConsultationIdCheck,
    getConsultationProductOptions,
    getIdentityCheckPassed,
  ],
  (answers = {}, idCheck = {}, productOptions = {}, identityCheckPassed) => {
    const { condition = {}, medicalHistory = {} } = answers;
    const { product = [] } = productOptions;
    const gpData = medicalHistory.gpName
      ? {
          name: medicalHistory.gpName,
          address: {
            address1: medicalHistory.gpAddress1,
            address2: medicalHistory.gpAddress2,
            city: medicalHistory.gpCity,
            county: medicalHistory.gpCounty,
            postcode: medicalHistory.gpPostcode,
          },
        }
      : {};
    const patient = identityCheckPassed
      ? {}
      : {
          firstname: idCheck.firstName,
          middlename: "",
          lastname: idCheck.lastName,
          phone: idCheck.phone,
          dob: idCheck.dob,
          address: {
            address1: idCheck.address1,
            address2: idCheck.address2,
            city: idCheck.city,
            county: idCheck.county,
            postcode: idCheck.postcode,
          },
        };

    return {
      type: "new",
      applicable_skus: [FIN30X1.id, MIN65.id],
      selected_skus: product.map((id) => ({ product_id: id, frequency: 1 })),
      consultation: [
        ...parseConsultationAnswers(condition),
        ...parseConsultationAnswers(medicalHistory),
      ],
      patient,
      gp: gpData,
    };
  }
);

export const getFormattedFollowUpConsultation = createSelector(
  [getConsultationAnswers],
  (answers = {}) => {
    const { condition = {}, terms = {} } = answers;
    const gpData = terms.gpName
      ? {
          name: terms.gpName,
          address: {
            address1: terms.gpAddress1,
            address2: terms.gpAddress2,
            city: terms.gpCity,
            county: terms.gpCounty,
            postcode: terms.gpPostcode,
          },
        }
      : {};

    return {
      type: "repeat",
      applicable_skus: [FIN30X1.id, MIN65.id],
      consultation: [
        ...parseConsultationAnswers(condition),
        ...parseConsultationAnswers(terms),
      ],
      gp: gpData,
    };
  }
);
